/**
 * Per-chat streaming state. Each chat tracks its own stream independently,
 * so multiple chats can stream concurrently without blocking each other.
 *
 * Phase 4 adds a snapshot hook that fires whenever `lastSeq` advances on an
 * active stream. chat-store installs the hook at boot; it's no-op in tests.
 */

import { signal } from '@preact/signals';

export interface ChatStreamState {
  /** Durable backend turn identity. Frames from another turn are ignored. */
  turnId: string | null;
  provider: string;
  model: string;
  phase: string;
  streaming: boolean;
  content: string;
  startedAt: number | null;
  /** Monotonic chunk seq from server — used for replay idempotency (Phase 3). */
  lastSeq: number;
  abortController: AbortController | null;
  /** Chars queued (waiting for this chat to finish streaming before sending). */
  queuedChars: number;
  /**
   * Latest live-progress line (tool activity / narration from the daemon).
   * Transient: cleared with the rest of the state when the stream finalizes.
   */
  activity: string;
  /** User-visible commentary and reasoning summaries, never raw reasoning. */
  progress: string;
  progressBlocks: Array<{ kind: string; text: string }>;
  /** Latest gateway elapsed time and the browser time when it arrived. */
  elapsedSeconds: number | null;
  elapsedReceivedAt: number | null;
  /** Optional gateway telemetry. Kept null until the gateway sends it. */
  toolCalls: number | null;
  lastTool: string;
  /** Most recent chunk, activity, tool-use, or heartbeat arrival. */
  lastActivityAt: number | null;
  /** Most recent server-owned lifecycle heartbeat, independent of token output. */
  lastHeartbeatAt: number | null;
  /** The gateway reported this turn lost or the WS dropped mid-turn. */
  interrupted: boolean;
  /** Distinguishes a provisional transport blip from a terminal turn failure. */
  interruptionKind: 'transport' | 'turn_lost' | 'error' | null;
}

export const chatStreamStates = signal<Record<number, ChatStreamState>>({});

export function makeEmptyStreamState(): ChatStreamState {
  return {
    turnId: null,
    provider: '',
    model: '',
    phase: '',
    streaming: false,
    content: '',
    startedAt: null,
    lastSeq: 0,
    abortController: null,
    queuedChars: 0,
    activity: '',
    progress: '',
    progressBlocks: [],
    elapsedSeconds: null,
    elapsedReceivedAt: null,
    toolCalls: null,
    lastTool: '',
    lastActivityAt: null,
    lastHeartbeatAt: null,
    interrupted: false,
    interruptionKind: null,
  };
}

/**
 * Snapshot hook: invoked when an updateChatStream advances lastSeq on a
 * streaming chat. Installed by chat-store to write to IDB (debounced).
 * Absent in tests unless explicitly wired.
 */
type SnapshotHook = (chatId: number, content: string, lastSeq: number) => void;
let _snapshotHook: SnapshotHook | null = null;
export function setSnapshotHook(fn: SnapshotHook | null): void {
  _snapshotHook = fn;
}

export function getChatStream(chatId: number): ChatStreamState {
  return chatStreamStates.value[chatId] ?? makeEmptyStreamState();
}

export function updateChatStream(
  chatId: number,
  patch: Partial<ChatStreamState>,
): void {
  const current = getChatStream(chatId);
  const next = { ...current, ...patch };
  chatStreamStates.value = {
    ...chatStreamStates.value,
    [chatId]: next,
  };

  if (
    _snapshotHook &&
    next.streaming &&
    patch.lastSeq != null &&
    patch.lastSeq > current.lastSeq
  ) {
    _snapshotHook(chatId, next.content, next.lastSeq);
  }
}

export function clearChatStream(chatId: number): void {
  if (!(chatId in chatStreamStates.value)) return;
  const next = { ...chatStreamStates.value };
  delete next[chatId];
  chatStreamStates.value = next;
}

/** True if any chat is currently streaming. */
export function anyChatStreaming(): boolean {
  const states = chatStreamStates.value;
  for (const id in states) {
    if (states[id].streaming) return true;
  }
  return false;
}
